import React, {useEffect, useState} from 'react'

import {Field, Form, Formik, FormikHelpers} from 'formik'
import {Col, Container, Row} from 'react-bootstrap'
import toast from 'react-hot-toast'
import {useTranslation} from 'react-i18next'
import AsyncSelect from 'react-select/async'

import KurzyLayout from './KurzyLayout'
import {useContentWrapper} from '../common/ContentWrapperProvider'
import FormErrorMessage from '../common/FormErrorMessage'
import {loadOptionsForCompanySelector, money} from '../common/helpers'
import NiceButton from '../common/NiceButton'
import { RequiredMark } from '../common/RequiredMark'
import {StyledInfoPageHeaderWrapper} from '../common/styledComponents'
import axiosClient from '../service/axiosClient'
import {OrderItemDto} from '../types/OrderItemDto'

function CartPage() {
    const contentWrapper = useContentWrapper()
    const {t} = useTranslation()
    const [product, setProduct] = useState<OrderItemDto | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [orderFinished, setOrderFinished] = useState(false)

    const defaultValues = {
        businessName: '',
        firstName: '',
        lastName: '',
        street: '',
        city: '',
        zipCode: '',
        icDph: '',
        dic: '',
        ico: '',
        phone: '',
        email: '',
        isCompany: false,
        vatPayer: false,
        dph7aParagraph: false,
        total: product?.price || 0,
        dphValue: 0,
        totalWithoutDph: product?.price || 0,
        totalWithDph: product?.price || 0,
        itemId: product?.id || 0,
    }

    const submit = async (values: typeof defaultValues, actions: FormikHelpers<typeof defaultValues>) => {
        setIsLoading(true)
        const dto = {
            businessName: values.businessName,
            firstName: values.firstName,
            lastName: values.lastName,
            street: values.street,
            city: values.city,
            zipCode: values.zipCode,
            ico: values.ico,
            dic: values.dic,
            icDph: values.icDph,
            phone: values.phone,
            email: values.email,
            vatPayer: values.vatPayer,
            dph7aParagraph: values.dph7aParagraph,
            itemId: values.itemId,
            createdAt: new Date().toISOString(),
        }

        try {
            await axiosClient.post('/order', dto)

            if (values.total && values.total > 0) {
                // platený produkt → presmerovanie na GoPay
                const initGoPayResponse = await axiosClient.post('/gopay/initialization/one-time-payment', dto)
                const redirectUrl = initGoPayResponse.data.gwUrl
                window.location.href = redirectUrl
            } else {
                // produkt zadarmo → len vytvorenie objednávky
                localStorage.removeItem('selectedProduct')
                setProduct(null)
                setOrderFinished(true)
                toast.success('Objednávka bola úspešne vytvorená')
            }
        } catch (error) {
            console.error(error)
            toast.error('Objednávku sa nepodarilo vytvoriť.')
        } finally {
            setIsLoading(false)
            actions.resetForm()
        }
    }

    const validate = (values: typeof defaultValues) => {
        const errors: any = {}
        if (!values.firstName?.trim()) errors.firstName = t('required_field_error')
        if (!values.lastName?.trim()) errors.lastName = t('required_field_error')

        if (values.isCompany) {
            if (!values.businessName?.trim()) errors.businessName = t('required_field_error')
            if (!values.ico?.toString().trim()) errors.ico = t('required_field_error')
            if (!values.dic?.toString().trim()) errors.dic = t('required_field_error')
            if ((values.vatPayer || values.dph7aParagraph) && !values.icDph?.trim()) {
                errors.icDph = t('required_field_error')
            }
        } else {
            if (!values.street?.trim()) errors.street = t('required_field_error')
            if (!values.city?.trim()) errors.city = t('required_field_error')
            if (!values.zipCode?.trim()) errors.zipCode = t('required_field_error')
        }

        return errors
    }

    useEffect(() => {
        contentWrapper.setNoBootstrapColumns(true)

        const savedProductJson = localStorage.getItem('selectedProduct')
        if (savedProductJson) {
            const savedProduct = JSON.parse(savedProductJson)
            setProduct(savedProduct)
        }

        return () => {
            contentWrapper.setNoBootstrapColumns(false)
        }
    }, [])

    return (
        <KurzyLayout>
            <StyledInfoPageHeaderWrapper>
                <Container>
                    <Row>
                        <Col>
                            <h1 className="font-vollkorn fs-55 color-pink-primary">Pokladňa</h1>
                        </Col>
                    </Row>
                </Container>
            </StyledInfoPageHeaderWrapper>

            <Container>
                {orderFinished ? (
                    <div className="rounded-content-body mt-4 text-center">
                        <h3 className="font-vollkorn mb-3">
                            👏 Ďakujeme, vašu objednávku sme prijali!
                        </h3>
                        <p className="fs-5">
                            Čoskoro vás budeme kontaktovať s ďalšími informáciami.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="rounded-content-body mt-3">
                            <h4 className="text-uppercase font-vollkorn text-left mb-3">Košík</h4>
                            {product ? (
                                <div className="d-flex justify-content-between">
                                    <span>1× {product.name}</span>
                                    <div className="text-end">
                                        <strong className="font-vollkorn">
                                            {product.price ? money.format(product.price) : 'bezplatne'}
                                        </strong>
                                        {product.installmentBuying && <div className="text-muted small">(možnosť úhrady na 3 splátky)</div>}
                                    </div>
                                </div>
                            ) : (
                                <div>Váš košík je prázdny.</div>
                            )}
                        </div>

                        {product && (
                            <div className="rounded-content-body mt-3">
                                <Row>
                                    <Col>
                                        <h4 className="text-uppercase font-vollkorn text-left mb-3">Údaje</h4>

                                        <Formik
                                            initialValues={defaultValues}
                                            enableReinitialize
                                            validate={validate}
                                            onSubmit={submit}
                                        >
                                            {({values, setFieldValue}) => (
                                                <Form>
                                                    <Row>
                                                        <Col>
                                                            <div className="form-group">
                                                                <label htmlFor="firstName">Meno<RequiredMark/></label>
                                                                <Field
                                                                    id="firstName"
                                                                    name="firstName"
                                                                    className="form-control"
                                                                />
                                                                <FormErrorMessage name="firstName"/>
                                                            </div>
                                                        </Col>
                                                        <Col>
                                                            <div className="form-group">
                                                                <label htmlFor="lastName">Priezvisko<RequiredMark/></label>
                                                                <Field
                                                                    id="lastName"
                                                                    name="lastName"
                                                                    className="form-control"
                                                                />
                                                                <FormErrorMessage name="lastName"/>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <div className="form-group form-check">
                                                        <Field
                                                            type="checkbox"
                                                            id="isCompany"
                                                            className="form-check-input"
                                                            name="isCompany"
                                                        />
                                                        <label
                                                            className="form-check-label"
                                                            htmlFor="isCompany"
                                                        >
                                                            Nakupujem na firmu
                                                        </label>
                                                    </div>

                                                    {values.isCompany && (
                                                        <>
                                                            <div className="form-group">
                                                                <label htmlFor="businessName">Názov firmy<RequiredMark/></label>

                                                                <AsyncSelect
                                                                    placeholder="Vyhľadajte spoločnosť"
                                                                    className="async-select"
                                                                    classNamePrefix="async-select"
                                                                    loadingMessage={() => <>Vyhľadávam...</>}
                                                                    noOptionsMessage={(inputValue) => (
                                                                        <>
                                                                            {inputValue.inputValue.length
                                                                                ? 'Nenašli sa žiadne výsledky'
                                                                                : 'Začnite písať...'}
                                                                        </>
                                                                    )}
                                                                    onChange={(item) => {
                                                                        setFieldValue('businessName', item?.label)
                                                                        setFieldValue('ico', item?.value?.cin)
                                                                        setFieldValue('dic', item?.value?.tin)
                                                                        setFieldValue('icDph', item?.value?.vatin)
                                                                        setFieldValue('street', item?.value?.formatted_street)
                                                                        setFieldValue('city', item?.value?.municipality)
                                                                        setFieldValue('zipCode', item?.value?.postal_code)

                                                                        setFieldValue('vatPayer', false)
                                                                        setFieldValue('dph7aParagraph', false)

                                                                        if (item?.value?.vatin_paragraph === '§4') {
                                                                            setFieldValue('vatPayer', true)
                                                                        }
                                                                        if (
                                                                            item?.value?.vatin_paragraph === '§7' ||
                                                                            item?.value?.vatin_paragraph === '§7a'
                                                                        ) {
                                                                            setFieldValue('dph7aParagraph', true)
                                                                        }
                                                                    }}
                                                                    cacheOptions
                                                                    loadOptions={loadOptionsForCompanySelector}
                                                                />

                                                                <Field
                                                                    id="businessName"
                                                                    name="businessName"
                                                                    className="form-control d-none"
                                                                />
                                                                <FormErrorMessage name="businessName"/>
                                                            </div>

                                                            <div className="form-group">
                                                                <label htmlFor="ico">IČO<RequiredMark/></label>
                                                                <Field id="ico" name="ico" className="form-control"/>
                                                                <FormErrorMessage name="ico"/>
                                                            </div>
                                                            <div className="form-group">
                                                                <label htmlFor="dic">DIČ<RequiredMark/></label>
                                                                <Field id="dic" name="dic" className="form-control"/>
                                                                <FormErrorMessage name="dic"/>
                                                            </div>

                                                            <div className="form-group form-check">
                                                                <input
                                                                    onChange={() => {
                                                                        setFieldValue('vatPayer', false)
                                                                        setFieldValue('dph7aParagraph', false)
                                                                    }}
                                                                    checked={!values.vatPayer && !values.dph7aParagraph}
                                                                    type="checkbox"
                                                                    id="vatPayerFalse"
                                                                    className="form-check-input"
                                                                />
                                                                <label
                                                                    className="form-check-label"
                                                                    htmlFor="vatPayerFalse"
                                                                >
                                                                    Subjekt nie je platiteľ DPH
                                                                </label>
                                                            </div>

                                                            <div className="form-group form-check">
                                                                <input
                                                                    onChange={() => {
                                                                        setFieldValue('vatPayer', true)
                                                                        setFieldValue('dph7aParagraph', false)
                                                                    }}
                                                                    checked={values.vatPayer && !values.dph7aParagraph}
                                                                    type="checkbox"
                                                                    id="vatPayerTrue"
                                                                    className="form-check-input"
                                                                />
                                                                <label
                                                                    className="form-check-label"
                                                                    htmlFor="vatPayerTrue"
                                                                >
                                                                    Subjekt je platiteľ DPH
                                                                </label>
                                                            </div>

                                                            <div className="form-group form-check">
                                                                <input
                                                                    onChange={() => {
                                                                        setFieldValue('vatPayer', false)
                                                                        setFieldValue('dph7aParagraph', true)
                                                                    }}
                                                                    checked={values.dph7aParagraph}
                                                                    type="checkbox"
                                                                    id="dph7aParagraph"
                                                                    className="form-check-input"
                                                                />
                                                                <label
                                                                    className="form-check-label"
                                                                    htmlFor="dph7aParagraph"
                                                                >
                                                                    Subjekt je registrovaný podľa §7/§7a
                                                                </label>
                                                            </div>

                                                            {(values.vatPayer || values.dph7aParagraph) && (
                                                                <div className="form-group">
                                                                    <label htmlFor="icDph">IČ DPH<RequiredMark/></label>
                                                                    <Field
                                                                        id="icDph"
                                                                        name="icDph"
                                                                        className="form-control"
                                                                    />
                                                                    <FormErrorMessage name="icDph"/>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}

                                                    <h4 className="text-left font-vollkorn mb-2">Adresa</h4>
                                                    <Row>
                                                        <Col lg="8">
                                                            <div className="form-group">
                                                                <label htmlFor="city">Mesto<RequiredMark/></label>
                                                                <Field
                                                                    id="city"
                                                                    name="city"
                                                                    className="form-control"
                                                                />
                                                                <FormErrorMessage name="city"/>
                                                            </div>
                                                        </Col>
                                                        <Col lg="4">
                                                            <div className="form-group">
                                                                <label htmlFor="zipCode">PSČ<RequiredMark/></label>
                                                                <Field
                                                                    id="zipCode"
                                                                    name="zipCode"
                                                                    className="form-control"
                                                                />
                                                                <FormErrorMessage name="zipCode"/>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <div className="form-group">
                                                        <label htmlFor="street">Ulica<RequiredMark/></label>
                                                        <Field
                                                            id="street"
                                                            name="street"
                                                            className="form-control"
                                                        />
                                                        <FormErrorMessage name="street"/>
                                                    </div>

                                                    <h4 className="text-left font-vollkorn mb-2">Kontaktné údaje</h4>
                                                    <Row>
                                                        <Col>
                                                            <div className="form-group">
                                                                <label htmlFor="phone">Telefón</label>
                                                                <Field
                                                                    id="phone"
                                                                    name="phone"
                                                                    className="form-control"
                                                                />
                                                                <FormErrorMessage name="phone"/>
                                                            </div>
                                                        </Col>
                                                        <Col>
                                                            <div className="form-group">
                                                                <label htmlFor="email">Email</label>
                                                                <Field
                                                                    id="email"
                                                                    name="email"
                                                                    className="form-control"
                                                                />
                                                                <FormErrorMessage name="email"/>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <NiceButton type="submit" isLoading={isLoading}>
                                                        {values.total > 0 ? 'Zaplatiť' : 'Objednať'}
                                                    </NiceButton>
                                                </Form>
                                            )}
                                        </Formik>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </>
                )}
            </Container>
        </KurzyLayout>
    )
}

export default CartPage
